import { Toast } from "@tento-ui/components/toast/toast";
import { Tracing } from "@tento-core/tracing";
import { type AuthScopedCalendarDisplay, EventWriteError } from "@tento-lona";
import type { EventItem } from "@tento-lona/sheets";

/**
 * How the event surfaces (details panel, context menu) tell the user that a
 * write they issued was refused. The SDK refuses a write to a calendar the
 * account cannot write before anything paints (`EventWriteError`, reason
 * `calendar_read_only`); the server refuses others. Either way the write
 * rejects, and the surface hands the rejection to a `Report` — never leaves
 * it unhandled, which is how a refused delete once vanished silently.
 */
export namespace EventWriteRefusal {
  /**
   * Tells the user `action` ("delete the event") on `event` was refused with
   * `error`. A host passes its calendar recovery (a reconnect prompt when
   * the account's grant is what refused); `toast` is the default. A
   * rejection that is not an `Error` arrives wrapped in one.
   */
  export type Report = (event: EventItem, action: string, error: Error) => void;

  /**
   * `Report` for a write that has no event yet (a create): tells the user
   * `action` into `calendar` was refused with `error`. The host passes its
   * calendar recovery; `toastForCalendar` is the default.
   */
  export type CalendarReport = (calendar: AuthScopedCalendarDisplay, action: string, error: Error) => void;

  /** The reason an `EventWriteError` carries for a calendar the account cannot write. */
  export const READ_ONLY_REASON = "calendar_read_only";

  /** What the user reads when the calendar is read-only. */
  export const READ_ONLY_MESSAGE = "This calendar is read-only. The event can't be changed here.";

  /** Whether `error` is the refusal of a write to a read-only calendar. */
  export function isReadOnly(error: Error): boolean {
    return error instanceof EventWriteError && error.reason === READ_ONLY_REASON;
  }

  /** The toast title for a refused `action`: the reason when it is known. */
  export function message(action: string, error: Error): string {
    return isReadOnly(error) ? READ_ONLY_MESSAGE : `Couldn't ${action}`;
  }

  /** The default `Report`: trace the refusal and toast its message. */
  export const toast: Report = (event, action, error) => {
    Tracing.warn("net")(`event write refused (${action})`, {
      eventId: event.id,
      error: String(error),
    });
    Toast.error(message(action, error));
  };

  /** The default `CalendarReport`: trace the refusal and toast its message. */
  export const toastForCalendar: CalendarReport = (calendar, action, error) => {
    Tracing.warn("net")(`calendar write refused (${action})`, {
      calendar: String(calendar[1].id),
      error: String(error),
    });
    Toast.error(message(action, error));
  };

  /**
   * Run `write`; on a rejection report it as a refused `action` and answer
   * false. True when the write succeeded.
   */
  export async function attempt<T>(
    event: EventItem,
    action: string,
    report: Report,
    write: () => Promise<T>,
  ): Promise<boolean> {
    return (await outcome(event, action, report, write)).written;
  }

  /** What `outcome` answers: the written value, or that the write was refused (and reported). */
  export type Outcome<T> = { readonly written: true; readonly value: T } | { readonly written: false };

  /**
   * `attempt` for a write whose result the surface goes on with (the saved
   * event, the copy): run `write`; on a rejection report it as a refused
   * `action`.
   */
  export async function outcome<T>(
    event: EventItem,
    action: string,
    report: Report,
    write: () => Promise<T>,
  ): Promise<Outcome<T>> {
    try {
      return { written: true, value: await write() };
    } catch (caught) {
      report(event, action, caught instanceof Error ? caught : new Error(String(caught)));
      return { written: false };
    }
  }
}
